import React from "react";
import { Stack, Typography } from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { usePromotion, useCreateQuest } from "@quest_editor/mocks";
import BasicInfoSection from "./BasicInfoSection";

const QuestRegister = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: promotionId } = useParams<{ id: string }>();

  const { data: promotionData } = usePromotion(promotionId);
  const promotion = promotionData?.promotion;

  const { createQuest } = useCreateQuest();

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const save = async () => {
    if (!promotionId) return null;
    return await createQuest({ promotionId, name, description });
  };

  const handleRegisterAndBackToDetail = async () => {
    if (!promotionId || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/promotion/${promotionId}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterAndGoToEdit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await save();
      const newQuestId = result?.data?.createQuest.id;
      if (!newQuestId) return;
      navigate(`/quest_editor/quest/${newQuestId}/edit`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="column" spacing={2}>
        <Typography variant="h6" component="h2">
          {t("quest_register.title")}
        </Typography>

        <BasicInfoSection
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
        />

        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ position: "relative" }}
        >
          <BackButton
            component={Link}
            to={`/quest_editor/promotion/${promotionId}`}
            sx={{ position: "absolute", left: 0 }}
          >
            {t("quest_register.button.back")}
          </BackButton>

          <Stack direction="row" spacing={2}>
            <ActionButton
              onClick={handleRegisterAndBackToDetail}
              disabled={isSubmitting}
            >
              {t("quest_register.button.register")}
            </ActionButton>
            <ActionButton
              onClick={handleRegisterAndGoToEdit}
              disabled={isSubmitting}
            >
              {t("quest_register.button.edit")}
            </ActionButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
export default QuestRegister;
